import { cn, formatCurrency } from '@/lib/utils'
import { TrendingUp, TrendingDown, DollarSign, Calendar, BarChart3 } from 'lucide-react'

interface BusinessPerformanceData {
  annualNetSales: number
  latestQuarterlyAvgNetSales: number
  latest6MonthsAvgNetSales: number
  recentSalesGrowth: number
}

interface BusinessPerformanceProps {
  data: BusinessPerformanceData
  title?: string
  className?: string
}

export function BusinessPerformance({
  data,
  title = 'Business Performance',
  className,
}: BusinessPerformanceProps) {
  const metrics = [
    {
      label: 'Annual Net Sales',
      value: data.annualNetSales,
      icon: DollarSign,
      format: 'currency' as const,
      color: 'blue',
    },
    {
      label: 'Quarterly Avg Net Sales',
      value: data.latestQuarterlyAvgNetSales,
      icon: BarChart3,
      format: 'currency' as const,
      color: 'purple',
    },
    {
      label: '6 Month Avg Net Sales',
      value: data.latest6MonthsAvgNetSales,
      icon: Calendar,
      format: 'currency' as const,
      color: 'teal',
    },
    {
      label: 'Recent Sales Growth',
      value: data.recentSalesGrowth,
      icon: data.recentSalesGrowth >= 0 ? TrendingUp : TrendingDown,
      format: 'percentage' as const,
      color: data.recentSalesGrowth >= 0 ? 'green' : 'red',
    },
  ]

  const colorClasses = {
    blue: { bg: 'bg-blue-50', icon: 'bg-blue-100 text-blue-600', text: 'text-blue-700' },
    purple: { bg: 'bg-purple-50', icon: 'bg-purple-100 text-purple-600', text: 'text-purple-700' },
    teal: { bg: 'bg-teal-50', icon: 'bg-teal-100 text-teal-600', text: 'text-teal-700' },
    green: { bg: 'bg-green-50', icon: 'bg-green-100 text-green-600', text: 'text-green-700' },
    red: { bg: 'bg-red-50', icon: 'bg-red-100 text-red-600', text: 'text-red-700' },
  }

  return (
    <div className={cn('bg-white rounded-xl border shadow-sm overflow-hidden', className)}>
      <div className="px-4 py-3 border-b bg-gray-50">
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
      </div>
      <div className="p-3 space-y-2">
        {metrics.map((metric, index) => {
          const Icon = metric.icon
          const colors = colorClasses[metric.color as keyof typeof colorClasses]

          return (
            <div
              key={index}
              className={cn(
                'flex items-center justify-between p-3 rounded-lg transition-all hover:scale-[1.01]',
                colors.bg
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn('p-2 rounded-lg', colors.icon)}>
                  <Icon className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium text-gray-700">{metric.label}</span>
              </div>
              <span className={cn('font-bold text-lg', colors.text)}>
                {metric.format === 'currency'
                  ? formatCurrency(metric.value)
                  : `${metric.value >= 0 ? '+' : ''}${(metric.value * 100).toFixed(2)}%`}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

